<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                <v-icon class="me-2">mdi-calendar-check</v-icon>
                Employee Daily Attendance Sheet
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="fetchSheet">
                  <v-row class="py-3 px-3" align="end" dense>
                    <v-col cols="12" md="3">
                      <v-date-input
                          input-format="dd/mm/yyyy"
                          v-model="filters.from_date"
                          label="From Date"
                          variant="outlined"
                          density="compact"
                          bg-color="grey-lighten-4"
                          prepend-icon=""
                          prepend-inner-icon="mdi-calendar"
                          hide-details="auto"
                          clearable
                          :max="filters.to_date"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-date-input
                          input-format="dd/mm/yyyy"
                          v-model="filters.to_date"
                          label="To Date"
                          variant="outlined"
                          density="compact"
                          bg-color="grey-lighten-4"
                          prepend-icon=""
                          prepend-inner-icon="mdi-calendar"
                          hide-details="auto"
                          clearable
                          :min="filters.from_date"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-autocomplete
                          v-model="filters.employee_id"
                          :items="employees"
                          :item-title="item => `${item.name}-(${item.code})` ?? 'N/A'"
                          item-value="id"
                          label="Employee (Optional)"
                          variant="outlined"
                          density="compact"
                          bg-color="grey-lighten-4"
                          :loading="employeesLoading"
                          clearable
                          hide-details="auto"
                          placeholder="Search employee"
                      
                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
/>
                    </v-col>
                    <v-col cols="12" md="3" class="d-flex align-end">
                      <v-btn
                          type="submit"
                          color="primary"
                          variant="flat"
                          :loading="loading"
                          prepend-icon="mdi-magnify"
                          density="compact"
                          height="40"
                      >
                        Search
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>

        <v-col cols="12">
          <v-expansion-panels v-model="reportPanel" class="mt-3">
            <v-expansion-panel>
              <v-expansion-panel-title
                  color="blue-grey-lighten-4"
                  class="panel-header"
                  expand-icon="mdi-chevron-down"
              >
                <v-icon class="me-2">mdi-table</v-icon>
                Attendance Details
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                        v-model="search"
                        append-inner-icon="mdi-magnify"
                        label="Search"
                        single-line
                        hide-details
                        density="compact"
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        class="pl-2"
                        clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                          v-if="sheetData.length > 0"
                          variant="outlined"
                          density="compact"
                          size="x-small"
                          divided
                      >
                        <v-btn
                            prepend-icon="mdi mdi-download"
                            variant="flat"
                            color="success"
                            @click="exportToExcel"
                            :disabled="!sheetData.length"
                        >EXCEL</v-btn
                        >
                        <v-btn
                            prepend-icon="mdi mdi-download"
                            variant="flat"
                            color="red"
                            @click="exportToPDF"
                            :disabled="!sheetData.length"
                        >PDF</v-btn
                        >
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12"></v-col>
                </v-row>

                <v-progress-linear
                    v-if="loading"
                    indeterminate
                    color="primary"
                    class="mb-4"
                />

                <v-alert
                    v-if="!loading && filteredSheetData.length === 0"
                    type="info"
                    variant="tonal"
                >
                  No attendance records found for the selected criteria.
                </v-alert>

                <div v-if="filteredSheetData.length">
                  <v-table density="comfortable" class="text-caption table-bordered">
                    <thead>
                    <tr>
                      <th># SL</th>
                      <th>Date</th>
                      <th>Employee Code</th>
                      <th>Employee Name</th>
                      <th>Department</th>
                      <th>Designation</th>
                      <th style="min-width: 150px;">Shift (In/Out)</th>
                      <th>Punch In</th>
                      <th>In Remarks</th>
                      <th>Punch Out</th>
                      <th>Out Remarks</th>
                      <th style="text-align: center;">Working (hrs)</th>
                      <th style="text-align: center;">Late (hrs)</th>
                      <th style="text-align: center;">Early (hrs)</th>
                      <th style="text-align: center;">OT (hrs)</th>
                      <th v-if="getCompanyInfo()?.id == 4">Actual Status</th>
                      <th>Status</th>
                      <th>Status Message</th>
                    </tr>
                    </thead>
                    <tbody >
                    <tr v-for="(row, index) in filteredSheetData" :key="`${row.employee_code}-${row.date}-${index}`">
                      <td>{{ index + 1 }}</td>
                      <td>{{ formatDateView(row.date) }}</td>
                      <td>{{ row.employee_code || '-' }}</td>
                      <td class="font-weight-medium">{{ row.employee_name || '-' }}</td>
                      <td>{{ row.department || '-' }}</td>
                      <td>{{ row.designation || '-' }}</td>
                      <td style="min-width: 150px;text-align: center;">
                        {{ formatTime(row.shift?.in_time) }} - {{ formatTime(row.shift?.out_time) }}
                      </td>
                      <td style="text-align: center;">{{ formatTime(row.punch_in) }}</td>
                      <td style="text-align: center;">{{ row.in_remarks }}</td>
                      <td style="text-align: center;">{{ formatTime(row.punch_out) }}</td>
                      <td style="text-align: center;">{{ row.out_remarks }}</td>
                      <td  style="text-align: center;">{{ row.working_hours }}</td>
                      <td style="text-align: center;">{{ (row.late_hours) }}</td>
                      <td style="text-align: center;">{{ (row.early_leaving_hours) }}</td>
                      <td style="text-align: center;">{{ (row.overtime_hours) }}</td>
                      <td v-if="getCompanyInfo()?.id == 4" class="text-left">
                        <v-chip
                            :color="row.actual_status == 'Present' ? 'green' : 'red'"
                            size="small"
                        >
                          {{ row.actual_status || '-' }}
                        </v-chip>
                      </td>
                      <td>
                        <v-chip
                            size="small"
                            :color="statusColor(row.status?.name)"
                            variant="text"
                        >
                          {{ row.status?.name || '-' }}
                        </v-chip>
                      </td>
                      <td>{{ row.message || '-' }}</td>
                    </tr>
                    </tbody>

                  </v-table>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue'
import { toast } from 'vue3-toastify'
import { useStore } from 'vuex'
import { formatDateView, formatDateDash } from "@/plugins/helpers";
import { getCompanyInfo } from '@/plugins/helpers'


const store = useStore()
const instance = getCurrentInstance()
const appContext = instance?.appContext

const employees = ref([])
const employeesLoading = ref(false)
const loading = ref(false)
const sheetData = ref([])
const panel = ref([0])
const reportPanel = ref([0])
const search = ref('')

const filters = reactive({
  from_date: '',
  to_date: '',
  employee_id: null
})

const getCurrentCompany = computed(() => {
  return store.getters.getCurrentCompany || {}
})

const getFileServerUrl = computed(() => {
  return store.getters.getFileServerUrl || ''
})

const formatEmployeeLabel = item => {
  if (!item) return ''
  return `${item.name || ''}${item.employee_code ? ` - ${item.employee_code}` : ''}`
}



const formatNumber = (value, fraction = 0) => {
  if (value === undefined || value === null || isNaN(value)) return '-'
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction
  })
}

const formatTime = (timeStr) => {
  if (!timeStr || timeStr === '-') return '-'

  // Handle time strings like "08:30:00" or "08:30"
  const timeParts = timeStr.split(':')
  if (timeParts.length < 2) return timeStr

  const hours = parseInt(timeParts[0], 10)
  const minutes = timeParts[1]

  if (isNaN(hours)) return timeStr

  const period = hours >= 12 ? 'PM' : 'AM'
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours

  return `${displayHours}:${minutes} ${period}`
}

// Filtered sheet data based on search
const filteredSheetData = computed(() => {
  if (!search.value) {
    return sheetData.value
  }

  const searchLower = search.value.toLowerCase()
  return sheetData.value.filter(row => {
    const employeeCode = (row.employee_code || '').toLowerCase()
    const employeeName = (row.employee_name || '').toLowerCase()
    const department = (row.department || '').toLowerCase()
    const designation = (row.designation || '').toLowerCase()
    return employeeCode.includes(searchLower) ||
        employeeName.includes(searchLower) ||
        department.includes(searchLower) ||
        designation.includes(searchLower)
  })
})

// Computed totals
const totals = computed(() => {
  return sheetData.value.reduce(
      (acc, row) => {
        acc.working_hours += Number(row.working_hours) || 0
        acc.late_minutes += Number(row.late_minutes) || 0
        acc.early_leaving_minutes += Number(row.early_leaving_minutes) || 0
        acc.overtime_hours += (Number(row.overtime_minutes) || 0) / 60
        return acc
      },
      {
        working_hours: 0,
        late_minutes: 0,
        early_leaving_minutes: 0,
        overtime_hours: 0
      }
  )
})

// Computed totals for filtered data
const filteredTotals = computed(() => {
  return filteredSheetData.value.reduce(
      (acc, row) => {
        acc.working_hours += Number(row.working_hours) || 0
        acc.late_minutes += Number(row.late_minutes) || 0
        acc.early_leaving_minutes += Number(row.early_leaving_minutes) || 0
        acc.overtime_hours += (Number(row.overtime_minutes) || 0) / 60
        return acc
      },
      {
        working_hours: 0,
        late_minutes: 0,
        early_leaving_minutes: 0,
        overtime_hours: 0
      }
  )
})

const statusColor = status => {
  switch (status) {
    case 'Present':
      return 'success'
    case 'Late Check In':
    case 'Late Check In & Early Checkout':
      return 'warning'
    case 'Early Checkout':
      return 'purple'
    case 'Absent':
      return 'error'
    case 'Leave':
      return 'blue'
    case 'Weekend':
    case 'Holiday':
      return 'teal'
    default:
      return 'grey'
  }
}

const setDefaultDates = () => {
  const today = new Date()
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
  filters.from_date = firstDay
  filters.to_date = today
}

const loadEmployees = async () => {
  employeesLoading.value = true
  try {
    const response = await store.dispatch('getEmployees')
    employees.value = response.data.data || []
  } catch (error) {
    employees.value = []
    toast.error(error.message || 'Failed to load employees')
  } finally {
    employeesLoading.value = false
  }
}

const fetchSheet = async () => {

  loading.value = true
  try {
    const params = {
      from_date: formatDateDash(filters.from_date),
      to_date: formatDateDash(filters.to_date),
      employee_id: filters.employee_id || undefined
    }

    const response = await store.dispatch('getEmployeeDailyAttendanceSheet', params)
    if (response.success) {
      sheetData.value = response.data || []
    } else {
      sheetData.value = []
      toast.error(response.message || 'Failed to fetch attendance sheet')
    }
  } catch (error) {
    sheetData.value = []
    toast.error(error.response?.data?.message || error.message || 'Failed to fetch attendance sheet')
  } finally {
    loading.value = false
  }
}

// Export to Excel
const exportToExcel = () => {
  if (!sheetData.value.length) {
    toast.warning('No data to export')
    return
  }

  const data = [
    ['Employee Daily Attendance Sheet'],
    [`From Date: ${formatDateView(filters.from_date)} to ${formatDateView(filters.to_date)}`]
  ]

  if (filters.employee_id) {
    const selectedEmployee = employees.value.find(e => e.id === filters.employee_id)
    if (selectedEmployee) {
      data.push([`Employee: ${selectedEmployee.name || ''}${selectedEmployee.employee_code ? ` (${selectedEmployee.employee_code})` : ''}`])
    }
  }

  data.push([], ['# SL', 'Date', 'Employee Code', 'Employee Name', 'Department', 'Designation', 'Shift (In/Out)', 'Punch In', 'Punch Out', 'Working Hours', 'Late (min)', 'Early (min)', 'OT (hrs)','Actual Status', 'Status', 'Remarks'], [])

  // Add data rows
  sheetData.value.forEach((row, index) => {
    data.push([
      index + 1,
      formatDateView(row.date),
      row.employee_code || '-',
      row.employee_name || '-',
      row.department || '-',
      row.designation || '-',
      `${formatTime(row.shift?.in_time)} - ${formatTime(row.shift?.out_time)}`,
      formatTime(row.punch_in) || '-',
      formatTime(row.punch_out) || '-',
      formatNumber(row.working_hours, 2),
      formatNumber(row.late_minutes),
      formatNumber(row.early_leaving_minutes),
      formatNumber(row.overtime_minutes / 60, 2),
      row.actual_status || '-',
      row.status?.name || '-',
      row.message || '-'
    ])
  })

  // Add totals row
  if (sheetData.value.length > 0) {
    data.push([])
    data.push([
      '', '', '', '', '', '', '','Total:',
      formatNumber(totals.value.working_hours, 2),
      formatNumber(totals.value.late_minutes),
      formatNumber(totals.value.early_leaving_minutes),
      formatNumber(totals.value.overtime_hours, 2),
      '', '',''
    ])
  }

  const proxy = appContext?.config?.globalProperties
  if (proxy?.$exportTableToExcel && proxy?.$fileDate) {
    proxy.$exportTableToExcel(data, 'employee_daily_attendance_sheet_' + proxy.$fileDate())
  }
}

const exportToPDF = async () => {

  if (!filteredSheetData.value.length) {

    toast.warning(
        'No data to export'
    )

    return
  }


  const company =
      getCurrentCompany.value

  const fileServerUrl =
      getFileServerUrl.value


  const selectedEmployee =
      filters.employee_id
          ? employees.value.find(
              e => e.id === filters.employee_id
          )
          : null


  const printWindow = window.open(
      '',
      '_blank',
      `width=${screen.width}, height=${screen.height}`
  )


  const printContent = `

    <!DOCTYPE html>

    <html>

      <head>

        <meta charset="UTF-8">

        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0"
        >

        <meta
          http-equiv="X-UA-Compatible"
          content="ie=edge"
        >

        <title>
          Employee Daily Attendance Sheet
        </title>


        <link
          href="https://cdn.jsdelivr.net/npm/@mdi/font@7.4.47/css/materialdesignicons.min.css"
          rel="stylesheet"
        >

        <style>

          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          @page {
            size: A4 landscape;
            margin: 0;
          }

          html,
          body {
            margin: 0;
            padding: 0;
            width: 100%;
            height: auto;
          }

          body {
            font-family: Arial, Helvetica, sans-serif;
            background: #fff;
            color: #000;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .report-container {
            width: 100%;
          }

          table.master-table {
            width: 100%;
            border-collapse: collapse;
          }

          .company-info {
            flex: 1;
            min-width: 0;
          }

          .company-name {
            font-size: 13px;
            font-weight: bold;
            text-transform: uppercase;
            margin-bottom: 4px;
          }

          .company-text {
            font-size: 11px;
            margin: 2px 0;
            line-height: 1.35;
          }

          .logo-container {
            width: 160px;
            display: flex;
            justify-content: flex-end;
            align-items: flex-start;
          }

          .logo-container img {
            max-height: 75px;
            max-width: 150px;
            object-fit: contain;
          }

          .header-row td {
            padding: 15px 32px 10px 32px;
            border-bottom: 2px solid #eee;
          }

          .footer-row td {
            padding: 2px 2px;
            border-top: 1px solid #eee;
          }

          .invoice-footer {
            margin-top: auto;
            position: fixed;
            left: 0;
            bottom: 0px;
            width: 100%;
          }

          .invoice-footer p {
            font-size: 11px;
            font-weight: 500;
            margin: 3px 0;
            display: flex;
            align-items: center;
            gap: 4px;
            line-height: 1.25;
          }

          .report-title {
            font-size: 16px;
            text-align: center;
            text-decoration: underline;
            font-weight: bold;
            margin: 10px 0 15px 0;
          }

          .report-info {
            display: flex;
            justify-content: space-between;
            margin-bottom: 12px;
            font-size: 12px;
          }

          .report-info-left {
            text-align: left;
          }

          .report-info-right {
            text-align: right;
          }

          .report-info p {
            margin: 3px 0;
          }

          table.attendance-table {
            width: 100%;
            border-collapse: collapse;
            border-spacing: 0;
            font-size: 10px;
            background: #fff;
          }

          .attendance-table thead {
            display: table-header-group;
          }


          .attendance-table tfoot {
            display: table-footer-group;
          }

          .attendance-table th,
          .attendance-table td {
            border: 1px solid #000;
            padding: 5px 4px;
            vertical-align: middle;
          }

          .attendance-table th {
            background: #e0e0e0;
            text-align: center;
            font-weight: bold;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .attendance-table td {
            text-align: left;
          }


          .text-center {
            text-align: center;
          }

          .text-right {
            text-align: right;
          }

          .total-row {
            font-weight: bold;
            background: #f5f5f5;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .total-row td {
            font-weight: bold;
          }

          .attendance-table tr {
            page-break-inside: avoid;
          }

          .attendance-table {
            page-break-inside: auto;
          }


          .attendance-table tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }

          @media print {
            body {
              margin: 0;
              padding: 0;
            }

            .header-row {
              display: table-header-group;
            }

            .footer-row {
              display: table-footer-group;
            }

            .attendance-table thead {
              display: table-header-group;
            }

            .attendance-table tr {
              page-break-inside: avoid;
            }

          }

        </style>

      </head>


      <body>

        <div class="report-container">
          <table class="master-table">
            <thead>
              <tr>
                <td colspan="100%">
                  <div>
                    <div style="display: flex; justify-content: flex-end; align-items: flex-start; padding-bottom: 10px; margin-bottom: 16px;">
                      <img
                        src="${fileServerUrl}${company.logo}"
                        class="logo-img"
                        alt="Logo"
                        style="max-height: 80px;"
                      />
                    </div>
                  </div>
                </td>
              </tr>
            </thead>

            <tbody>
              <tr class="body-row">
                <td>

                  <div class="report-title">
                    EMPLOYEE DAILY ATTENDANCE SHEET
                  </div>


                  <div class="report-info">
                    <div class="report-info-left">
                      <p>
                        <strong>Period:</strong> ${formatDateView(filters.from_date)} to ${formatDateView(filters.to_date)} </p>

                      ${selectedEmployee
      ? `
                            <p>
                              <strong>Employee:</strong>
                              ${selectedEmployee.name || ''}
                              ${ selectedEmployee.employee_code
          ? ` (${selectedEmployee.employee_code})`
          : ''
      }
                            </p>
                          `
      : ''
  }

                    </div>

                    <div class="report-info-right">
                      <p><strong>Total Records:</strong>${filteredSheetData.value.length}</p>
                    </div>

                  </div>

                  <table class="attendance-table">
                    <thead>
                      <tr>
                        <th>SN</th>
                        <th>Date</th>
                        <th>Employee Code</th>
                        <th>Employee Name</th>
                        <th>Department</th>
                        <th>Designation</th>
                        <th>Shift</th>
                        <th>Punch In</th>
                        <th>Punch Out</th>
                        <th>Working Hours</th>
                        <th>Late (min)</th>
                        <th>Early (min)</th>
                        <th>OT (hrs)</th>
                        <th>Actual Status</th>
                        <th>Status</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>

                    <tbody>
                      ${filteredSheetData.value.map((row, index) => `
                        <tr>
                          <td class="text-center">${index + 1}</td>
                          <td>${formatDateView(row.date)}</td>
                          <td>${row.employee_code || '-'}</td>
                          <td>${row.employee_name || '-'}</td>
                          <td>${row.department || '-'}</td>
                          <td>${row.designation || '-'}</td>
                          <td class="text-center">${formatTime(row.shift?.in_time)} - ${formatTime(row.shift?.out_time)}</td>
                          <td class="text-center">${formatTime(row.punch_in) || '-'}</td>
                          <td class="text-center">${formatTime(row.punch_out) || '-'}</td>
                          <td class="text-center">${formatNumber(row.working_hours, 2)}</td>
                          <td class="text-center">${formatNumber(row.late_minutes)}</td>
                          <td class="text-center">${formatNumber(row.early_leaving_minutes)}</td>
                          <td class="text-center">${formatNumber(row.overtime_minutes / 60, 2)}</td>
                          <td>${row.actual_status || '-'}</td>
                          <td>${row.status?.name || '-'}</td>
                          <td>${row.message || '-'}</td>
                        </tr>
                      `).join('')}
                    </tbody>

                    <tfoot>
                      <tr class="total-row">
                        <td colspan="9" class="text-right">Total:</td>
                        <td class="text-center">${formatNumber(filteredTotals.value.working_hours, 2)}</td>
                        <td class="text-center">${formatNumber(filteredTotals.value.late_minutes)}</td>
                        <td class="text-center">${formatNumber(filteredTotals.value.early_leaving_minutes)}</td>
                        <td class="text-center">${formatNumber(filteredTotals.value.overtime_hours, 2)}</td>
                        <td colspan="3"></td>
                      </tr>
                    </tfoot>
                  </table>

                </td>
              </tr>
            </tbody>

            <tfoot>
              <tr>
                <td colspan="100%" style="border:none">
                  <div style="width:100%;height: 100px;">&nbsp;</div>
                </td>
              </tr>
              <tr class="footer-row">
                <td>

                  <div class="invoice-footer" style="margin-left:10px; margin-bottom:10px; color: #343591;">
                <p style="font-size: 13px; font-weight: 500; display: flex; align-items: center; gap:4px">
                  <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                    <path d="M0 0h24v24H0z" fill="none" />
                    <path fill="currentColor" d="M12 11.5A2.5 2.5 0 0 1 9.5 9A2.5 2.5 0 0 1 12 6.5A2.5 2.5 0 0 1 14.5 9a2.5 2.5 0 0 1-2.5 2.5M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7" />
                  </svg>

                  <span>${company?.address}</span>
                </p>

                <p style="font-size: 13px; font-weight: 500; display: flex; align-items: center; gap:4px">
                  <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                    <path d="M0 0h24v24H0z" fill="none" />
                    <path fill="currentColor" d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24c1.12.37 2.33.57 3.57.57c.55 0 1 .45 1 1V20c0 .55-.45 1-1 1c-9.39 0-17-7.61-17-17c0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1c0 1.25.2 2.45.57 3.57c.11.35.03.74-.25 1.02z" />
                  </svg>

                  <strong>Phone: </strong>  +880 9610-993393, +880 2 222288648, www.remexingredient.com | www.remexcorp.com
                </p>
              </div>
                </td>
              </tr>
            </tfoot>
          </table>

        </div>

      </body>

    </html>
  `


  printWindow.document.write(
      printContent
  )

  printWindow.document.close()

  printWindow.focus()

  await new Promise(resolve =>
      setTimeout(resolve, 700)
  )

  printWindow.print()
  printWindow.close()
}

onMounted(async () => {
  setDefaultDates()
  await loadEmployees()
})
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}

.table-bordered :deep(th),
.table-bordered :deep(td) {
  border: 1px solid #000000 !important;
}

.table-bordered :deep(table) {
  border-collapse: collapse;
}

.panel-header {
  font-weight: 600;
}
</style>